import { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";

const buildSearchPrompts = (city = "your city") => [
  `Budget clubs near me in ${city}`,
  `Live DJ clubs tonight in ${city}`,
  `Rooftop clubs this weekend in ${city}`,
  `Bollywood night events in ${city}`,
  `Live music events near me`,
  `Trending artists for parties in ${city}`,
  `Clubs with happy hours in ${city}`,
  `Events by area or genre in ${city}`,
  `Lounges for birthday parties`,
  `EDM and techno nights in ${city}`,
  `Weekend events with music in ${city}`,
  `Book DJs and singers in ${city}`,
];

export default function AnimatedSearchBar({
  value,
  onChange,
  onKeyDown,
  name = "",
  placeholder = "Search by Area, Genre, Artist or Club",
  animatedPlaceholders,
  className = "",
  delay = "0.4s",
  isLoading = false,
}) {
  const selectedCity = useSelector((state) => state.citySelector.selectedCity);
  const userBaseCity = useSelector((state) => state.citySelector.userBaseCity);
  const promptCity = selectedCity || userBaseCity || "your city";
  const prompts = useMemo(() => {
    const promptList = animatedPlaceholders?.length
      ? animatedPlaceholders
      : buildSearchPrompts(promptCity);

    return promptList.filter(Boolean);
  }, [animatedPlaceholders, promptCity]);
  const [isFocused, setIsFocused] = useState(false);
  const [promptIndex, setPromptIndex] = useState(0);
  const [typedText, setTypedText] = useState("");
  const hasValue = value !== undefined && `${value}`.length > 0;
  const shouldAnimatePlaceholder = !isFocused && !hasValue && prompts.length > 0;
  const orbIconDelays = isLoading ? ["0s", "0.6s", "1.2s"] : ["0s", "1s", "2s"];
  const wrapperClassName = [
    "search-location",
    "asb-wrapper",
    isLoading ? "asb-wrapper--loading" : "",
    "wow",
    "fadeInUp",
    className,
  ]
    .filter(Boolean)
    .join(" ");

  useEffect(() => {
    if (!shouldAnimatePlaceholder) {
      setTypedText("");
      return undefined;
    }

    const currentPrompt = prompts[promptIndex] || placeholder;

    if (typedText === currentPrompt) {
      const holdTimeoutId = window.setTimeout(() => {
        setTypedText("");
        setPromptIndex((currentIndex) => (currentIndex + 1) % prompts.length);
      }, 3000);

      return () => window.clearTimeout(holdTimeoutId);
    }

    const timeoutId = window.setTimeout(() => {
      setTypedText(currentPrompt.slice(0, typedText.length + 1));
    }, 40);

    return () => window.clearTimeout(timeoutId);
  }, [placeholder, promptIndex, prompts, shouldAnimatePlaceholder, typedText]);

  useEffect(() => {
    setPromptIndex(0);
    setTypedText("");
  }, [promptCity]);

  return (
    <div className={wrapperClassName} data-wow-delay={delay}>
      <input
        type="search"
        name={name}
        value={value}
        className="form-control asb-input"
        placeholder={shouldAnimatePlaceholder ? typedText : ""}
        onChange={onChange}
        onKeyDown={onKeyDown}
        onFocus={() => setIsFocused(true)}
        onBlur={() => setIsFocused(false)}
      />
      <button className="asb-orb" type="submit" aria-label="Search">
        <div className="asb-orb__glow" />
        <div className="asb-orb__ring" />
        <div className="asb-orb__inner">
          <div className="asb-orb__shine" />
          <div className="asb-orb__icon" style={{ animationDelay: orbIconDelays[0] }}>
            <i className="fa fa-robot" aria-hidden="true" />
          </div>
          <div className="asb-orb__icon" style={{ animationDelay: orbIconDelays[1] }}>
            <i className="fa fa-map-marker-alt" aria-hidden="true" />
          </div>
          <div className="asb-orb__icon" style={{ animationDelay: orbIconDelays[2] }}>
            <i className="fa fa-music" aria-hidden="true" />
          </div>
        </div>
      </button>
    </div>
  );
}
